import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    DwCacheService, DwCacheStorageLocation, DwConfigService, DwConfigServiceToken,
    DwEventService, DwOrmData, DwOrmDataService, DwOrmDataServiceToken, DwQueryBuilderBase,
    DwSecurityTopics, DwSecurityUserService,
    DwLookupInfo, DwMetaDataService, DwMetaDataServiceToken,
} from '@devwareapps/devware-cap';
import { combineLatest, defer, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { FitnessMediaService } from './fitness-media.service';
import { PublicCatalog, PUBLIC_CATALOG_ITEMS } from '../models/public-catalog.models';
import { ProductPreview } from '../models/product-preview.models';
import {
    AppMetaData, ChallengeDefinitionEntity,
    ChallengeDefinitionProductEntity,
    DwProductEntity, DwProductMediaEntity,
    DwProductSizeEntity, DwProductTypeEntity,
    DwCodeSetEntity, DwCodeValueEntity, AppMetaDataLookups,
    UserProfileEntity, UserChallengeEntity,
    DwCountryEntity, DwStateProvinceEntity,
} from '../../../meta-data/app-meta-data.service';

export interface ChallengeCatalog {
    challenge: ChallengeDefinitionEntity;
    registrationProduct: DwProductEntity;
    products: Array<{ product: DwProductEntity; includedInChallenge: boolean }>;
}

export interface RegistrationOptions {
    years: DwLookupInfo[];
    sources: DwCodeValueEntity[];
}

export function challengeRegistrationPrice(challenge: Pick<ChallengeDefinitionEntity, 'EntryFee'>): number | null {
    const price = challenge && challenge.EntryFee;
    return price != null && Number.isFinite(price) && price >= 0 ? price : null;
}

/** Fitness reference data only. Checkout always revalidates availability and prices. */
@Injectable({ providedIn: 'root' })
export class FitnessReferenceDataService {
    private readonly refreshTopic = 'Fitness.ReferenceData.Refresh';
    private securityRevision = 0;

    constructor(
        @Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        private cache: DwCacheService,
        private security: DwSecurityUserService,
        @Inject(DwConfigServiceToken) private config: DwConfigService,
        private events: DwEventService,
        private media: FitnessMediaService,
        private http: HttpClient,
        @Inject(DwMetaDataServiceToken) private metadata: DwMetaDataService,
    ) {
        // Separate in-flight queries as well as completed caches on login/logout/emulation.
        this.security.securityContextChanged().subscribe(() => this.securityRevision++);
        Array.from(new Set([...PUBLIC_CATALOG_ITEMS, 'YearsStreaking', 'dw_CodeSet', 'dw_CodeValue', 'UserProfile', 'UserChallenge'])).forEach(item =>
            this.events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item))
                .subscribe(() => this.securityRevision++));
    }

    getChallengeDefinitions(): Observable<ChallengeDefinitionEntity[]> {
        const query = AppMetaData.ChallengeDefinition.CreateQueryBuilder()
            .addFields(a => [
                a.ChallengeDefinitionId, a.Name,
                a.Description, a.IsCurrent,
                a.ChallengeStartDate, a.ChallengeEndDate,
                a.RegistrationStartDate, a.RegistrationEndDate,
                a.StoreStartDate, a.StoreEndDate,
                a.ProductId, a.HasShirt, a.EntryFee,
                a.StoreShippingMessage, a.AfterOrderShippingMessage,
            ])
            .addOrderByDesc(a => a.ChallengeStartDate)
            .addPrefetch(r => r.ChallengeMedia);
        return this.cached<ChallengeDefinitionEntity>('challenges', query, ['ChallengeDefinition', 'ChallengeMedia']);
    }

    getDefaultCheckoutCountry(): Observable<DwCountryEntity> {
        const query = AppMetaData.DwCountry.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.CountryShortName, 'US'));
        return this.cached<DwCountryEntity>('checkout-default-country', query, ['dw_Country'])
            .pipe(map(rows => rows.length === 1 ? rows[0] : null));
    }

    getCheckoutCountry(countryId: number): Observable<DwCountryEntity> {
        if (!countryId) { return of(null); }
        const query = AppMetaData.DwCountry.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.CountryId, countryId));
        return this.cached<DwCountryEntity>('checkout-country-' + countryId, query, ['dw_Country'])
            .pipe(map(rows => rows.length === 1 ? rows[0] : null));
    }

    getCheckoutStates(countryId: number): Observable<DwStateProvinceEntity[]> {
        if (!countryId) { return of([]); }
        const query = AppMetaData.DwStateProvince.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.CountryId, countryId));
        return this.cached<DwStateProvinceEntity>('checkout-states-' + countryId, query, ['dw_StateProvince']);
    }

    getCheckoutState(countryId: number, stateId: number): Observable<DwStateProvinceEntity> {
        if (!countryId || !stateId) { return of(null); }
        const query = AppMetaData.DwStateProvince.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.CountryId, countryId));
        query.addFilterAnd(f => f.Equal(a => a.StateProvinceId, stateId));
        return this.cached<DwStateProvinceEntity>('checkout-state-' + countryId + '-' + stateId, query, ['dw_StateProvince'])
            .pipe(map(rows => rows.length === 1 ? rows[0] : null));
    }

    getActiveChallenge(): Observable<ChallengeDefinitionEntity> {
        return this.getPublicCatalog().pipe(map(catalog => ({ ...AppMetaData.ChallengeDefinition.CreateEntity(), ...catalog.Challenge })));
    }

    getPublicCatalog(): Observable<PublicCatalog> {
        return defer(() => {
            const scope = this.scopeKey();
            const url = this.config.coreConfig.apiRoot.replace(/\/$/, '') + '/fitness/challenges/current/catalog';
            return this.cache.getWithOptions<PublicCatalog>(`Fitness.Reference.${scope}.public-catalog`,
                this.http.get<PublicCatalog>(url).pipe(take(1), map(catalog => {
                    this.requireSameScope(scope);
                    this.requireActiveChallenge([{ ...AppMetaData.ChallengeDefinition.CreateEntity(), ...catalog.Challenge }]);
                    return catalog;
                })), {
                    storageLocation: DwCacheStorageLocation.memory,
                    expirationInMinutes: 30,
                    cacheInvalidationsTopics: [this.refreshTopic, DwSecurityTopics.SECURITY_CHANGED,
                        ...PUBLIC_CATALOG_ITEMS.map(item => DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item))],
                }).pipe(take(1), map(catalog => {
                    this.requireSameScope(scope);
                    this.media.seed(catalog.Media);
                    return catalog;
                }));
        });
    }

    getCompletedRegistrationCount(challengeId: number): Observable<number> {
        const url = this.config.coreConfig.apiRoot.replace(/\/$/, '') + '/fitness/challenges/' + challengeId + '/registration-count';
        return this.http.get<{ Count?: number; count?: number }>(url).pipe(take(1), map(result => {
            const count = result.Count ?? result.count;
            if (!Number.isInteger(count) || count < 0) { throw new Error('Invalid signup total.'); }
            return count;
        }));
    }

    getProducts(): Observable<DwProductEntity[]> {
        return this.getPublicCatalog().pipe(map(catalog => catalog.Products.map(p => ({ ...AppMetaData.DwProduct.CreateEntity(), ...p }))));
    }

    getChallengeProducts(): Observable<ChallengeDefinitionProductEntity[]> {
        return this.getPublicCatalog().pipe(map(catalog => catalog.ChallengeProducts.map(p => ({ ...AppMetaData.ChallengeDefinitionProduct.CreateEntity(), ...p }))));
    }

    getProductSizes(): Observable<DwProductSizeEntity[]> {
        return this.getPublicCatalog().pipe(map(catalog => catalog.Products.reduce(
            (sizes, product) => sizes.concat(product.Sizes.map(s => ({ ...AppMetaData.DwProductSize.CreateEntity(), ...s }))), [] as DwProductSizeEntity[])));
    }

    getProductTypes(): Observable<DwProductTypeEntity[]> {
        return this.getPublicCatalog().pipe(map(catalog => catalog.Products
            .filter((product, index, products) => products.findIndex(p => p.ProductTypeId === product.ProductTypeId) === index)
            .map(product => ({ ...AppMetaData.DwProductType.CreateEntity(), ProductTypeId: product.ProductTypeId, ProductType: product.ProductType }))));
    }

    getRegistrationOptions(): Observable<RegistrationOptions> {
        // YearsStreaking is canonical; the duplicated YearsStreaking code set is legacy.
        const years = defer(() => {
            const scope = this.scopeKey();
            return this.metadata.getLookup(AppMetaDataLookups.YearsStreakingAllItems).pipe(take(1), map(rows => {
                this.requireSameScope(scope);
                return rows;
            }));
        });
        const sets = AppMetaData.DwCodeSet.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.CodeSetName, 'MarketingSource'))
            .addFields(a => [a.CodeSetId, a.CodeSetName]);
        const values = AppMetaData.DwCodeValue.CreateQueryBuilder();
        values.addFilterAnd(f => f.Equal(a => a.IsActive, true));
        values.addFields(a => [a.CodeValueId, a.CodeSetId,
            a.Display, a.Meaning, a.CodeValueOrder]).addOrderBy(a => a.CodeValueOrder);
        return combineLatest([years,
            this.cached<DwCodeSetEntity>('registration-marketing-code-set', sets, ['dw_CodeSet']),
            this.cached<DwCodeValueEntity>('registration-code-values', values, ['dw_CodeValue'])]).pipe(take(1),
            map(([yearOptions, codeSets, codeValues]) => {
                const options = (name: string) => {
                    const set = codeSets.find(s => s.CodeSetName === name);
                    if (!set) { throw new Error('Registration code set is missing: ' + name); }
                    return codeValues.filter(v => v.CodeSetId === set.CodeSetId);
                };
                return { years: yearOptions, sources: options('MarketingSource') };
            }));
    }

    getActiveChallengeCatalog(): Observable<ChallengeCatalog> {
        return this.getPublicCatalog().pipe(map(catalog => {
            const registrationProduct = catalog.Products.find(p => p.ProductId === catalog.Challenge.ProductId);
            if (!registrationProduct || challengeRegistrationPrice(catalog.Challenge) == null) {
                throw new Error('The active challenge requires an active registration product and a valid entry fee.');
            }
            return { challenge: { ...AppMetaData.ChallengeDefinition.CreateEntity(), ...catalog.Challenge },
                registrationProduct: { ...AppMetaData.DwProduct.CreateEntity(), ...registrationProduct },
                products: catalog.ChallengeProducts.map(link => {
                    const product = catalog.Products.find(p => p.ProductId === link.ProductId);
                    return { product: product ? { ...AppMetaData.DwProduct.CreateEntity(), ...product } : null,
                        includedInChallenge: link.IncludedInChallenge === true };
                }).filter(link => !!link.product) };
        }));
    }

    getBuyerChallengeHistory(): Observable<UserChallengeEntity[]> {
        return defer(() => {
            const user = this.security.securityContext && this.security.securityContext.ApplicationUser;
            if (!this.security.securityState.isAuthenticated || !user || !user.UserId) { return of([]); }
            const profiles = AppMetaData.UserProfile.CreateQueryBuilder();
            profiles.addFilterAnd(f => f.Equal(a => a.UserId, user.UserId));
            profiles.addFields(a => [a.UserProfileId]);
            return this.cached<UserProfileEntity>('registration-profile', profiles, ['UserProfile']).pipe(switchMap(rows => {
                if (rows.length > 1) { throw new Error('Multiple profiles were returned for this account.'); }
                if (!rows.length) { return of([]); }
                const history = AppMetaData.UserChallenge.CreateQueryBuilder();
                history.addFilterAnd(f => f.Equal(a => a.UserProfileId, rows[0].UserProfileId));
                history.addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true));
                history.addFields(a => [a.UserChallengeId, a.ChallengeDefinitionId,
                    a.YearsStreakingCv, a.CreatedDateTime])
                    .addOrderByDesc(a => a.CreatedDateTime).addOrderByDesc(a => a.UserChallengeId);
                return this.cached<UserChallengeEntity>('registration-history', history, ['UserChallenge']);
            }));
        });
    }

    getProductPreview(productId: number): Observable<ProductPreview> {
        return this.getProductPreviews([productId]).pipe(map(previews=>previews[productId] || null));
    }
    /** Historical streak graphics must not depend on active store-product availability. */
    getStreakProductMedia(productIds: number[]): Observable<DwProductMediaEntity[]> {
        const ids = Array.from(new Set(productIds.filter(id => Number.isSafeInteger(id) && id > 0)));
        if (!ids.length) { return of([]); }
        const query = AppMetaData.DwProductMedia.CreateQueryBuilder()
            .addFields(a => [a.ProductMediaId, a.ProductId, a.MediaId, a.IsDefault, a.MediaOrder])
            .addFilterAnd(f => f.InList(a => a.ProductId, ids))
            .addOrderByDesc(a => a.IsDefault)
            .addOrderBy(a => a.MediaOrder).addOrderBy(a => a.ProductMediaId);
        return this.orm.executeQuery<DwProductMediaEntity>(query).pipe(take(1));
    }
    getChallengeMedia(challengeId: number): Observable<{ [key: string]: number }> {
        return this.getPublicCatalog().pipe(map(catalog => {
            const keys: { [key: string]: number } = {};
            if (catalog.Challenge.ChallengeDefinitionId !== challengeId) { return keys; }
            catalog.Challenge.Media.forEach(asset => {
                if (asset.MediaId > 0 && asset.MediaKeyName) { keys[asset.MediaKeyName] = asset.MediaId; }
            });
            return keys;
        }));
    }

    getProductPreviews(productIds: number[]): Observable<{ [id: number]: ProductPreview }> {
        if (!productIds.length) { return of({}); }
        return this.getPublicCatalog().pipe(switchMap(catalog => {
            const previews: { [id: number]: ProductPreview } = {};
            catalog.Products.filter(product => productIds.includes(product.ProductId)).forEach(product => {
                const usable = product.Media.filter(image => image.MediaId > 0).slice().sort((a, b) =>
                    Number(b.IsDefault === true) - Number(a.IsDefault === true) ||
                    (a.MediaOrder || 0) - (b.MediaOrder || 0) || a.MediaId - b.MediaId);
                const images = usable.filter((image, index) => usable.findIndex(row => row.MediaId === image.MediaId) === index)
                    .map(image => ({ mediaId: image.MediaId, name: image.MediaName, url: '' }));
                if (images.length) { previews[product.ProductId] = { ...images[0], images }; }
            });
            return this.media.urls(Object.keys(previews).reduce((ids, id) => ids.concat(previews[id].images.map(image => image.mediaId)), [] as number[])).pipe(map(urls => {
                Object.keys(previews).forEach(id => {
                    previews[id].images.forEach(image => image.url = urls[image.mediaId]);
                    previews[id].url = urls[previews[id].mediaId];
                });
                return previews;
            }));
        }));
    }

    /** Invalidate this application's reference caches; does not clear server caches. */
    refresh(): void {
        // New generation prevents an older in-flight response from repopulating the new scope.
        this.securityRevision++;
        this.events.publishEvent(this.refreshTopic, {});
    }

    private requireActiveChallenge(challenges: ChallengeDefinitionEntity[]): ChallengeDefinitionEntity {
        const active = challenges.filter(challenge => challenge.IsCurrent === true);
        if (active.length !== 1) {
            throw new Error('Exactly one active challenge must be configured.');
        }
        return active[0];
    }

    private scopeKey(): string {
        const context = this.security.securityContext;
        return JSON.stringify([
            this.config.coreConfig.apiRoot, context && context.ApiRoot,
            context && context.AuthenticatedUser && context.AuthenticatedUser.UserId,
            context && context.ApplicationUser && context.ApplicationUser.UserId,
            this.securityRevision,
        ]);
    }

    private requireSameScope(scope: string): void {
        if (scope !== this.scopeKey()) {
            throw new Error('The security context changed while reference data was loading. Please reload.');
        }
    }

    private cached<T extends DwOrmData>(name: string, query: DwQueryBuilderBase<T>, items: string[]): Observable<T[]> {
        return defer(() => {
            const scope = this.scopeKey();
            return this.cache.getWithOptions<T[]>(`Fitness.Reference.${scope}.${name}`,
                this.orm.executeQuery<T>(query).pipe(take(1), map(rows => {
                    this.requireSameScope(scope);
                    return rows || [];
                })), {
                    storageLocation: DwCacheStorageLocation.memory,
                    expirationInMinutes: 1,
                    cacheInvalidationsTopics: [this.refreshTopic, DwSecurityTopics.SECURITY_CHANGED,
                        ...items.map(item => DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item))],
                }).pipe(take(1), map(rows => {
                    this.requireSameScope(scope);
                    return rows;
                }));
        });
    }
}
